(ns graph-ui.web.bus-client
  "CLJS WebSocket client for the ikc3 command bus (PLAN-graph-ui-v0-001-02 Phase W3).

   Port of resources/public/bus-client.js -- same EDN-envelope protocol over
   WebSocket text frames. One WS text frame = one envelope; the server broadcasts
   every bus event to all connected clients.

   Uses a closure-based factory (make-bus-client) instead of deftype/defrecord
   to avoid CLJS type inference issues with interop methods."
  (:require [cljs.reader :as reader]
            [graph-ui.web.activity :as activity]
            [graph-ui.web.busy :as busy]
            [graph-ui.web.nav :as nav]))

;; @feature F-platform-v0-002-02-command-bus
;; @implements FR-F-platform-v0-002-02-006
;; @adr ADR-ikc-v3-065-01-remote-bus-access
;; @adr ADR-ikc-v3-077-01-public-access-alatusresearch

;; ---------------------------------------------------------------------------
;; Handshake token (ADR-ikc-v3-077-01 §4). The token is entered once and kept
;; in localStorage; it rides the WebSocket handshake as a subprotocol, never
;; in a command payload and never in the URL. A browser WebSocket controls no
;; header except Sec-WebSocket-Protocol, so the token travels as the entry
;; "ikc3-token.<token>" beside the protocol name "ikc3-bus" — the server
;; echoes the protocol name back (dsl/contexts/bus.edn :networked-ws
;; :handshake). With no stored token the connection is exactly as before.
;; ---------------------------------------------------------------------------

(def token-storage-key
  "The one localStorage key the bus token lives under. To authenticate:
   localStorage.setItem('ikc3-bus-token', '<token>') and reload."
  "ikc3-bus-token")

(defn- stored-token []
  (try (.getItem js/localStorage token-storage-key)
       (catch :default _ nil)))

(def sign-in-reasons
  "The refusals a PERSON answers by signing in. A wrong machine token
   (:unauthorized) is not one of them: a person has no token to correct."
  #{:unauthenticated :session-unknown :session-expired})

(defn sign-in-href
  ;; CONCEPT: CONCEPT-ikc-v3-016-01-a-sign-in-session
  "Where a refused page goes, or nil to stay (ADR-ikc-v3-106-01). Pure, so
   the node tests drive it with no js/location.

   ONLY IN A SECURE CONTEXT. The session cookie is Secure by declaration
   (authz.edn :sign-in-session :cookie), and a browser does not keep a Secure
   cookie from a plain http page on another host. Measured on the deployment:
   http://192.168.0.190:4556 is such a page. A redirect there would sign the
   person in and hand back a page that is refused again, for ever. So a page
   that is not secure stays, and the console says why.

   NOT FROM THE SIGN-IN PAGE ITSELF, which never opens a bus socket in any
   case. The guard costs one comparison."
  [reason {:keys [secure? pathname search]}]
  (when (and secure?
             (contains? sign-in-reasons reason)
             (not= "/sign-in.html" pathname))
    (str "/sign-in.html?next="
         (js/encodeURIComponent (str (or pathname "/") (or search ""))))))

(defn own-refusal
  ;; CONCEPT: CONCEPT-ikc-v3-016-01-a-sign-in-session
  "PURE. `event` when it is the refusal of THIS connection, else nil.

   THE BUS PUBLISHES EACH REFUSAL TWICE. ikc.bus.ws-server/refuse-connection!
   sends it to the refused connection, and it emits it on the shared bus for
   observers such as Mission Control. So every open page receives the refusal
   of every other connection. Until 2026-10-02 each page acted on it: a tab
   with no sign-in session was refused every 3 seconds, and each refusal sent
   every signed-in page to the sign-in page.

   A REFUSED CONNECTION RECEIVES ITS REFUSAL FIRST, before any other message,
   and the bus then closes it. So a refusal is this connection's own only when
   `message-number` is 1. The caller acts on it only when the connection
   closes, so a refusal of another connection that arrives first on a healthy
   connection changes nothing."
  [message-number event]
  (when (and (= 1 message-number)
             (= :bus/intervention (:type event))
             (= :connection-refused (:result event)))
    event))

(defn- connection-refused!
  "A :bus/intervention with :result :connection-refused is the server's one
   readable refusal envelope before it closes the socket (ADR-ikc-v3-065-01
   decision 6). Surface it where a person can find it: the console, and a
   window-level flag.

   A PERSON WHO MUST SIGN IN IS TAKEN TO THE SIGN-IN PAGE (ADR-ikc-v3-106-01).
   Since that decision the bus refuses a stranger when people exist, and a
   page that only logged the refusal stayed on \"Connecting…\" for ever."
  [ev]
  (let [reason (some-> ev :reason :reason)
        href   (sign-in-href reason
                             {:secure?  (boolean (.-isSecureContext js/window))
                              :pathname (.. js/window -location -pathname)
                              :search   (.. js/window -location -search)})]
    (js/console.error "ikc3 bus: connection refused —"
                      (or (some-> ev :reason :message) (pr-str (:reason ev)))
                      (cond
                        href
                        "(going to the sign-in page)"

                        (contains? sign-in-reasons reason)
                        "(sign in on the https name of this deployment: a plain http page cannot keep the session cookie)"

                        :else
                        (str "(set localStorage." token-storage-key
                             " to a valid machine token and reload)")))
    (set! (.-ikc3BusRefused js/window)
          (clj->js {:reason (some-> reason str)
                    :message (some-> ev :reason :message)}))
    (when href
      (.assign (.-location js/window) href))))

(defn edn-encode
  "Encode a CLJS value as an EDN string (pr-str is EDN-compatible in CLJS)."
  [v]
  (pr-str v))

(defn edn-decode
  "Decode an EDN string into a CLJS value using cljs.reader."
  [s]
  (reader/read-string s))

(defn make-bus-client
  "Create a bus client connected to the WebSocket URL. Returns a JS object with
   methods: ready, dispatch, subscribe, emit, close, on-event.

   The connection opens immediately; call (.ready bus cb) to await it."
  [url]
  (let [ws-atom       (atom nil)
        seq-atom      (atom 0)
        pending-atom  (atom {})
        subs-atom     (atom [])
        on-event-atom (atom nil)

        next-id
        (fn [] (str "cljs-" (swap! seq-atom inc)))

        on-message
        ;; `message-number` counts the messages of ONE connection, from 1.
        ;; `own-refusal-atom` is that connection's own: a refusal of this
        ;; connection is kept there, and acted on when the connection closes.
        (fn [raw message-number own-refusal-atom]
          (try
            (let [env (edn-decode raw)]
              (case (:t env)
                :event
                (do
                  (when-let [ev (own-refusal message-number (:event env))]
                    (reset! own-refusal-atom ev))
                  (when-let [cb @on-event-atom]
                    (cb (:event env)))
                  (let [ev-type (-> env :event :type)
                        subs    @subs-atom]
                    (doseq [{:keys [pattern callback]} subs]
                      (when (or (nil? pattern) (= ev-type pattern))
                        (callback (:event env))))))

                :reply
                (when-let [cb (get @pending-atom (:id env))]
                  (swap! pending-atom dissoc (:id env))
                  (cb (:result env)))

                nil))
            (catch js/Error e
              (js/console.error "bus-client decode error:" e))))

        on-close
        (fn []
          (reset! ws-atom nil)
          ;; Nothing outstanding can ever be answered now, so the busy indicator
          ;; must stop claiming work is in progress — a spinner on a dead socket
          ;; is a lie the reader has no way to see through.
          (busy/drop-all!)
          (let [pending @pending-atom]
            (reset! pending-atom {})
            (doseq [[_ cb] pending]
              (cb {:error :disconnected}))))

        ;; THE SOCKET IS RE-OPENABLE (George, 2026-09-08: "Web page does not
        ;; reconnect with the server after bus restart - it does not function
        ;; until is reloaded"). It was one socket built inline in this `let`, so
        ;; there was nothing to call twice: on-close emptied ws-atom and the
        ;; client stayed dead until the page was reloaded.
        ;;
        ;; EVERYTHING ELSE WAS ALREADY BUILT FOR THIS. `subs-atom` survives a
        ;; close, `send-wanted!` resends the whole set and says in its own
        ;; comment that it runs "on EVERY open ... or kept across a reconnect",
        ;; and on-close already answers outstanding callbacks with
        ;; {:error :disconnected}. Only the re-open was missing.
        ;; Deliberate .close() must NOT come back. Set by the "close" method.
        closing?      (atom false)
        attempts      (atom 0)
        on-reopen-atom (atom nil)
        send-wanted!
        (fn []
          (when-let [w @ws-atom]
            (let [pats (map :pattern @subs-atom)]
              (.send w (edn-encode
                        {:t :subscribe
                         ;; A nil pattern anywhere means this connection
                         ;; wants everything; the server reads nil that way.
                         :patterns (when (every? some? pats) (set pats))})))))
        open-socket!
        (fn open-socket! []
          (let [token (stored-token)
                ;; With a stored token, offer the protocol name plus the
                ;; token carrier; the server selects "ikc3-bus". With no
                ;; token, connect exactly as before — no subprotocols at all.
                ;;
                ;; READ AGAIN ON EVERY ATTEMPT, not captured once: a reconnect
                ;; may happen after a sign-in, and the token that was absent
                ;; when the page loaded is the one the new socket needs.
                ;; PER CONNECTION, so a reconnect starts again at message 1.
                message-count (atom 0)
                own-refusal   (atom nil)
                w (if (seq token)
                    (js/WebSocket. url
                                   (clj->js ["ikc3-bus"
                                             (str "ikc3-token." token)]))
                    (js/WebSocket. url))]
            (set! (.-onopen w)
                  (fn [_]
                    (let [reopen? (pos? @attempts)]
                      (reset! attempts 0)
                      (reset! ws-atom w)
                      ;; AND ON EVERY OPEN, so a subscription made before the
                      ;; socket was ready — or kept across a reconnect — reaches
                      ;; the server. The whole set is resent, so it is
                      ;; idempotent.
                      (send-wanted!)
                      ;; THE SOCKET IS BACK; THE SESSION IS NOT. A restarted bus
                      ;; holds no presentation for this browser, so re-attaching
                      ;; is the caller's job and not this namespace's — it knows
                      ;; the island and the session id, and this knows a URL.
                      (when reopen?
                        (when-let [cb @on-reopen-atom] (cb))))))
            (set! (.-onerror w) (fn [e] (js/console.error "bus-client WS error:" e)))
            (set! (.-onclose w)
                  (fn [_]
                    (on-close)
                    ;; THE BUS CLOSED A CONNECTION IT REFUSED. Only now is the
                    ;; refusal this page's own (own-refusal).
                    (when-let [ev @own-refusal]
                      (connection-refused! ev))
                    (when-not @closing?
                      ;; BACKOFF, CAPPED. A bus restart takes tens of seconds
                      ;; (Datomic, the schema, the island seeds), so a fixed
                      ;; fast retry would spend that whole time failing. The cap
                      ;; is 10s because a reader who walked away should find the
                      ;; page live again within one, not after an hour of
                      ;; doubling.
                      (let [n (swap! attempts inc)
                            ms (min 10000 (* 500 (bit-shift-left 1 (min 5 (dec n)))))]
                        (js/setTimeout open-socket! ms)))))
            (set! (.-onmessage w)
                  (fn [e] (on-message (.-data e) (swap! message-count inc) own-refusal)))
            w))
        ;; Opened here so `ready` has something to watch for. The binding is
        ;; unused past this point — every handler reaches the socket through
        ;; ws-atom, which a reconnect replaces.
        _ws (open-socket!)]


    (js-obj
     "ready" (fn [cb]
               (if (some? @ws-atom)
                 (cb)
                 (add-watch ws-atom :ready
                            (fn [_ _ _ new-ws]
                              (when (some? new-ws)
                                (remove-watch ws-atom :ready)
                                (cb))))))

     "dispatch" (fn [command cb]
                  (let [id (next-id)
                        t0 (js/performance.now)
                        ;; Dogfood observability: record every dispatch with its
                        ;; round-trip ms into the client activity ring-buffer, so
                        ;; the :activity-log widget shows the live command stream
                        ;; and a slow handler (the 30-40s island-select cascade)
                        ;; stands out by its ms (2026-07-18).
                        wrapped (fn [result]
                                  ;; The in-flight registry empties FIRST, so the
                                  ;; indicator clears in the same tick the reply
                                  ;; lands rather than one render behind it.
                                  (busy/finish! id)
                                  (let [dt (- (js/performance.now) t0)]
                                    (activity/record! {:kind :dispatch
                                                       :type (:type command)
                                                       ;; For :ui/read (and any command that names a
                                                       ;; :source), show WHICH source — otherwise a
                                                       ;; pile of ":ui/read" is unattributable (the very
                                                       ;; gap that hid the slow reads, 2026-07-18).
                                                       :detail (some-> (:source command) str)
                                                       :ms   (js/Math.round dt)}))
                                  ;; THE TRAIL IS NOTED HERE, on the reply, and
                                  ;; not at the three call sites that select a
                                  ;; page. Those sites are a wikilink, an outline
                                  ;; row and a search result today, and the next
                                  ;; one would have to remember. A reply is also
                                  ;; the only place that knows the page was really
                                  ;; reached — a refused command must leave no
                                  ;; entry (graph-ui.web.nav).
                                  (when (and (= :ui/select-page (:type command))
                                             (not (:error result)))
                                    (nav/record! (:page-id command)))
                                  (when cb (cb result)))]
                    (if-let [w @ws-atom]
                      (do
                        (swap! pending-atom assoc id wrapped)
                        ;; INSIDE the branch, not beside it: a start! for a
                        ;; request that was never sent would leave an hourglass
                        ;; turning for the rest of the session.
                        (busy/start! id command (js/Date.now))
                        (.send w (edn-encode {:t :dispatch :id id :command command})))
                      ;; NO SOCKET IS A REFUSAL, ANSWERED AT ONCE (PR #40 review,
                      ;; 2026-09-20). Until now the callback was parked in
                      ;; pending-atom and nothing ever sent or answered it: the
                      ;; close handler refuses only what was pending when a LIVE
                      ;; socket closed. A field then kept its words and showed no
                      ;; reason, and the arch prompt froze with :sending true.
                      ;; The words stay on screen, the reason is shown, and the
                      ;; person sends again when the bus is back — the same act
                      ;; the reconnect of 2026-09-08 was measured with.
                      (when cb (cb {:error :disconnected})))))

     ;; PLAN-graph-ui-v0-004-01 Phase 4 (:on-submit editor): the wire
     ;; protocol has had a general :introspect envelope since Phase 7
     ;; (ikc.bus.ws-server/handle-envelope!, {:t :introspect} ->
     ;; {:commands #{...} :queries #{...}}) — this is the first CLJS
     ;; caller, not a new server-side capability. Replies come back as an
     ;; ordinary {:t :reply :id ... :result ...} envelope, same generic
     ;; on-message handling every dispatch reply already uses.
     "introspect" (fn [cb]
                    (let [id      (next-id)
                          ;; Counted like any other round trip. It is one, and the
                          ;; :on-submit editor's first paint waits for it.
                          wrapped (fn [result] (busy/finish! id) (when cb (cb result)))]
                      (swap! pending-atom assoc id wrapped)
                      (when-let [w @ws-atom]
                        (busy/start! id {:type :bus/introspect} (js/Date.now))
                        (.send w (edn-encode {:t :introspect :id id})))))

     ;; SUBSCRIBING TELLS THE SERVER, since 2026-08-23. Until then the pattern
     ;; was client-side only: the server pushed EVERY bus event to every client
     ;; and this handler threw away what nobody had asked for. One page click
     ;; delivered 82 frames, of which 19 were :frames-lib/get-frame and about 48
     ;; were :bus/command-dispatched — the server's own internal query traffic
     ;; (PLAN-ikc-v3-019-01).
     ;;
     ;; THE WHOLE SET IS RESENT EACH TIME, not a delta. The server holds one set
     ;; per connection, so a resend is idempotent and a reconnect that replays
     ;; the subscriptions converges — a delta protocol would have to be correct
     ;; about ordering and about what a dropped frame means.
     ;;
     ;; A nil pattern (subscribe to everything) makes the whole connection
     ;; unfiltered, which is the honest reading of what that caller asked for.
     "subscribe" (fn [pattern callback]
                   (let [sub {:pattern pattern :callback callback}]
                     (swap! subs-atom conj sub)
                     (send-wanted!)
                     (fn unsubscribe []
                       (swap! subs-atom #(vec (remove #{sub} %)))
                       (send-wanted!))))

     "emit" (fn [event]
              (when-let [w @ws-atom]
                (.send w (edn-encode {:t :emit :event event}))))

     "close" (fn []
               ;; DELIBERATE, so it must not come back. Every other close is a
               ;; bus that went away, and those reconnect.
               (reset! closing? true)
               (when-let [w @ws-atom]
                 (.close w 1000 "client closed")
                 (reset! ws-atom nil)))

     ;; WHAT TO DO WHEN THE SOCKET COMES BACK. Called on a RE-open only, never
     ;; on the first one — the first open is the caller's own connect path and
     ;; already does this work. A restarted bus holds no presentation for this
     ;; browser, so the caller re-attaches; this namespace knows a URL and
     ;; nothing about islands or sessions.
     "on-reopen" (fn [callback]
                   (reset! on-reopen-atom callback))

     "on-event" (fn [callback]
                  (reset! on-event-atom callback)))))
